import { Link } from 'react-router';

import {
  AsyncSection,
  DataTable,
  PageHeader,
  Panel,
  useNotifications,
  type Column,
} from '../../components';
import { CertificateStatus, type A1CertificateResponse } from '../../api';
import { formatDate, formatDateTime } from '../../format';
import { PATHS } from '../../routes/paths';
import { useCertificates, useDeactivateCertificate } from '../queries';
import {
  CERTIFICATES_COPY,
  CERTIFICATE_STATUS_LABELS,
  CERTIFICATE_STATUS_TONES,
} from './certificates';

/**
 * What the platform can sign with, and until when.
 *
 * Its own screen rather than a panel on `/equipe/credenciais`, and the address
 * says why: that screen is credentials a *partner* holds toward us, this one is
 * the credential *we* hold toward the government. They are revoked for
 * different reasons, by different hands, on different days -- and this one is
 * the administrator's alone, which the other is not.
 *
 * Installing one has moved to [`NewCertificatePage`](./NewCertificatePage.tsx),
 * behind the action in the header. What stays here is the reading: which
 * certificates exist, which are about to expire, and the one action that takes
 * one out of service.
 *
 * Two decisions shape it.
 *
 * **Expiry is the headline, not a column.** An A1 certificate lasts a year, and
 * replacing it means buying one from a certification authority, which takes
 * days. So a certificate inside 30 days of expiry, or past it, is said above the
 * table as a sentence rather than left as a word in a row somebody has to
 * notice. That is the whole reason the screen exists rather than a script.
 *
 * **Nothing here deletes.** Installing a replacement marks the previous
 * certificate for that CNPJ superseded and keeps both rows; deactivating stamps
 * a row and keeps it. Both remain listed with their status, because "what did
 * we sign April with?" is a question about a certificate that no longer counts.
 */
export function CertificatesPage() {
  const certificates = useCertificates();
  const deactivate = useDeactivateCertificate();
  const { notify } = useNotifications();

  const columns: readonly Column<A1CertificateResponse>[] = [
    {
      key: 'subject_name',
      header: CERTIFICATES_COPY.holder,
      render: (certificate) => certificate.subject_name,
    },
    {
      key: 'subject_cnpj',
      header: CERTIFICATES_COPY.cnpj,
      render: (certificate) => certificate.subject_cnpj_formatted,
    },
    {
      key: 'validity',
      header: CERTIFICATES_COPY.validity,
      render: (certificate) =>
        `${formatDate(certificate.not_valid_before)} – ${formatDate(certificate.not_valid_after)}`,
    },
    {
      key: 'status',
      header: CERTIFICATES_COPY.status,
      render: (certificate) => (
        <span className={`badge badge--${CERTIFICATE_STATUS_TONES[certificate.status]}`}>
          {CERTIFICATE_STATUS_LABELS[certificate.status]}
        </span>
      ),
    },
    {
      key: 'issuer_name',
      header: CERTIFICATES_COPY.issuer,
      render: (certificate) => certificate.issuer_name,
    },
    {
      key: 'fingerprint_sha256',
      header: CERTIFICATES_COPY.fingerprint,
      headerHint: CERTIFICATES_COPY.fingerprintHint,
      render: (certificate) => <code>{certificate.fingerprint_sha256}</code>,
    },
    {
      key: 'uploaded_at',
      header: CERTIFICATES_COPY.uploadedBy,
      render: (certificate) =>
        `${certificate.uploaded_by_display_name} · ${formatDateTime(certificate.uploaded_at)}`,
    },
    {
      key: 'actions',
      header: CERTIFICATES_COPY.actions,
      align: 'end',
      render: (certificate) => {
        // Only a certificate that is still in service can be taken out of it.
        // A superseded or already-stopped one has nothing left to stop, and the
        // backend refuses both -- offering the button would be offering a 409.
        const inService =
          certificate.status === CertificateStatus.ACTIVE ||
          certificate.status === CertificateStatus.EXPIRING_SOON ||
          certificate.status === CertificateStatus.EXPIRED;
        if (!inService) {
          return <span className="table__muted">—</span>;
        }
        // Every button is disabled while one request is in flight, but only the
        // row being acted on says so: one shared `isPending` rendered as a label
        // would tell somebody that three certificates are being stopped when one
        // is.
        const stopping = deactivate.isPending && deactivate.variables === certificate.id;
        return (
          <button
            type="button"
            className="button button--danger"
            disabled={deactivate.isPending}
            onClick={() => {
              if (!window.confirm(CERTIFICATES_COPY.deactivateConfirm)) {
                return;
              }
              deactivate.mutate(certificate.id, {
                onSuccess: () => notify('success', CERTIFICATES_COPY.deactivated),
                onError: () => notify('error', CERTIFICATES_COPY.deactivateFailed),
              });
            }}
          >
            {stopping ? CERTIFICATES_COPY.deactivating : CERTIFICATES_COPY.deactivate}
          </button>
        );
      },
    },
  ];

  return (
    <div className="page">
      <PageHeader
        title={CERTIFICATES_COPY.title}
        description={CERTIFICATES_COPY.description}
        actions={
          <Link className="button button--primary" to={PATHS.staffCertificateNew}>
            {CERTIFICATES_COPY.uploadTitle}
          </Link>
        }
      />

      <Panel title={CERTIFICATES_COPY.listTitle} description={CERTIFICATES_COPY.listDescription}>
        <AsyncSection
          state={certificates}
          loadingLabel={CERTIFICATES_COPY.loading}
          errorTitle={CERTIFICATES_COPY.loadError}
          isEmpty={(data) => data.items.length === 0}
          emptyTitle={CERTIFICATES_COPY.empty}
          emptyDescription={CERTIFICATES_COPY.emptyDescription}
        >
          {(data) => {
            const expiring = data.items.filter(
              (item) => item.status === CertificateStatus.EXPIRING_SOON,
            ).length;
            const expired = data.items.filter(
              (item) => item.status === CertificateStatus.EXPIRED,
            ).length;
            return (
              <>
                {expired > 0 && (
                  <p className="panel__footnote panel__footnote--warning" role="status">
                    {CERTIFICATES_COPY.attentionExpired(expired)}
                  </p>
                )}
                {expiring > 0 && (
                  <p className="panel__footnote panel__footnote--warning" role="status">
                    {CERTIFICATES_COPY.attentionExpiring(expiring)}
                  </p>
                )}
                <DataTable
                  id="certificates"
                  caption={CERTIFICATES_COPY.listCaption}
                  columns={columns}
                  rows={data.items}
                  rowKey={(certificate) => certificate.id}
                />
              </>
            );
          }}
        </AsyncSection>
      </Panel>
    </div>
  );
}
